import { useState } from "react";

export type HistoryEntry = {
  question: string;
  answer: string | null;
  typed?: boolean;
};

type Props = {
  entries: HistoryEntry[];
};

/**
 * Slim collapsible Q&A history. Closed by default — a small "↑ Show history (N)"
 * affordance in the top header. When opened, drops down a scroll-clipped panel
 * with each prior Q&A in compact form.
 *
 * Stays out of the way visually. Lets the subject re-read what they've said
 * without breaking the single-question focal point.
 */
export function HistoryDrawer({ entries }: Props) {
  const [open, setOpen] = useState(false);
  if (entries.length === 0) return null;

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="text-[10px] uppercase tracking-[0.22em] text-ink-300 hover:text-ink-500 transition-colors flex items-center gap-1.5"
        aria-expanded={open}
        aria-controls="history-drawer"
      >
        <span className="inline-block transition-transform duration-300" style={{ transform: open ? "rotate(180deg)" : "rotate(0deg)" }}>
          ↑
        </span>
        <span>{open ? "Hide history" : `Show history (${entries.length})`}</span>
      </button>
      {open && (
        <div
          id="history-drawer"
          className="absolute top-8 left-0 right-0 sm:left-1/2 sm:right-auto sm:-translate-x-1/2 sm:w-[min(640px,90vw)] z-10 bg-parchment-50 border border-parchment-200 rounded-2xl shadow-2xl shadow-ink-900/10 p-6 max-h-[60vh] overflow-y-auto"
        >
          <ol className="space-y-6">
            {entries.map((entry, i) => (
              <li key={i} className="space-y-2">
                <p className="text-[10px] uppercase tracking-[0.22em] text-ink-300">Q{i + 1}</p>
                <p className="font-hand text-2xl leading-snug text-ink-900">{entry.question}</p>
                {entry.answer && (
                  <p className="font-display italic text-base leading-relaxed text-ink-700 pl-4 border-l border-parchment-300">
                    "{entry.answer}"
                    {entry.typed && (
                      <span className="ml-2 text-[10px] uppercase tracking-[0.18em] text-ink-300 not-italic">typed</span>
                    )}
                  </p>
                )}
              </li>
            ))}
          </ol>
        </div>
      )}
    </div>
  );
}
